import { observer } from "mobx-react-lite";
import type { ServerTimelineCluster as ServerTimelineClusterType } from "@/domains/documents/utils/timelineClustering";
import type { DocumentRowMapped, ExpandedClusterState } from "@/stores/documents/repository";
import { TimelineStem } from "./TimelineStem";
import { ServerTimelineDayGroup, type DocumentNodeProps } from "./ServerTimelineDayGroup";

/** A day bucket nobody has expanded yet — nothing fetched, nothing to page. */
const UNFETCHED_DAY: Pick<
  ExpandedClusterState,
  "documents" | "isLoading" | "hasMore" | "truncated"
> = {
  documents: [],
  isLoading: false,
  hasMore: false,
  truncated: false,
};

/**
 * What a day group needs to know about its own fetch state: the documents it
 * can show, whether its first page is still in flight, and whether the day
 * holds more than has been fetched.
 */
function dayGroupState(expanded: ExpandedClusterState | undefined) {
  const state = expanded ?? UNFETCHED_DAY;
  const isEmpty = state.documents.length === 0;
  return {
    documents: state.documents,
    isPending: !expanded || (state.isLoading && isEmpty),
    // A truncated day has more documents too — paging just gave up reaching
    // them, and the same button re-reads the day from the start. Ending the
    // group here instead would read as "that is the whole day", which is false.
    hasMore: state.hasMore || state.truncated,
    isLoadingMore: state.isLoading && !isEmpty,
  };
}

interface ServerTimelineDayBandProps {
  /** The day buckets, newest first. */
  clusters: ServerTimelineClusterType[];
  /** The documents fetched for a bucket so far, if it has been expanded. */
  getExpandedState: (cluster: ServerTimelineClusterType) => ExpandedClusterState | undefined;
  /** Fetch a day's next page of documents. */
  onLoadMore: (cluster: ServerTimelineClusterType) => void;
  /** Per-document card props, built by the timeline. */
  nodeProps: (doc: DocumentRowMapped) => DocumentNodeProps;
}

/**
 * ServerTimelineDayBand — the Days zoom band.
 *
 * Shows one card per document, never a cluster tile, so it lays its buckets
 * out in flow rather than on the fixed slot grid the coarser bands use: a
 * day's cards stack tightly on that day's side of the stem while consecutive
 * days are separated by a much wider gap, so one day reads as one group
 * whatever its cards' heights are.
 *
 * pt-8 leaves the first day's label clear of the stem's arrowhead.
 */
export const ServerTimelineDayBand = observer(function ServerTimelineDayBand({
  clusters,
  getExpandedState,
  onLoadMore,
  nodeProps,
}: ServerTimelineDayBandProps) {
  return (
    <div className="relative px-6 pb-6 pt-8">
      {/* Central stem, spanning the stacked day groups */}
      <div className="pointer-events-none absolute inset-y-2 left-1/2 -translate-x-1/2">
        <TimelineStem height="100%" />
      </div>

      <div className="relative flex flex-col gap-10">
        {clusters.map((cluster) => {
          const day = dayGroupState(getExpandedState(cluster));

          // A bucket the server counted but that resolves to no rows (a filter
          // narrowed it away) renders nothing rather than a stuck spinner.
          if (!day.isPending && day.documents.length === 0) return null;

          return (
            <ServerTimelineDayGroup
              key={cluster.id}
              cluster={cluster}
              {...day}
              onLoadMore={() => onLoadMore(cluster)}
              nodeProps={nodeProps}
            />
          );
        })}
      </div>
    </div>
  );
});
